import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy, doc, updateDoc, deleteDoc, Timestamp, where } from 'firebase/firestore';
import { db, functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { Shop, BusinessType } from '../types';
import { CountryMaster, AreaMaster } from '../types/master';

// Shops management page
const Shops: React.FC = () => {
  const [shops, setShops] = useState<Shop[]>([]);
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [countries, setCountries] = useState<CountryMaster[]>([]);
  const [areas, setAreas] = useState<AreaMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBusinessType, setFilterBusinessType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSubscription, setFilterSubscription] = useState<string>('all');
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openSyncResultDialog, setOpenSyncResultDialog] = useState(false);
  const [openExtendPlanDialog, setOpenExtendPlanDialog] = useState(false);
  const [syncResults, setSyncResults] = useState<any[]>([]);
  const [extendPlanTarget, setExtendPlanTarget] = useState<Shop | null>(null);
  const [extendPlanForm, setExtendPlanForm] = useState({
    extensionMonths: 1,
    newPlanId: '',
  });
  const [syncing, setSyncing] = useState(false);
  const [editingShop, setEditingShop] = useState<Shop | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Shop | null>(null);
  const [passwordChangeTarget, setPasswordChangeTarget] = useState<Shop | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [shopCodeError, setShopCodeError] = useState<string | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [formData, setFormData] = useState({
    shopCode: '',
    shopName: '',
    name: '',
    businessTypeCode: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    ownerPassword: '',
    ownerPasswordConfirm: '',
    postalCode: '',
    prefecture: '',
    city: '',
    address: '',
    building: '',
    phoneNumber: '',
    email: '',
    websiteUrl: '',
    description: '',
    planId: 'free',
    language: 'ja' as 'ja' | 'th' | 'en',
    timezone: 'Asia/Tokyo',
    currency: 'JPY' as 'JPY' | 'THB' | 'USD',
    mobileOrderEnabled: false,
    isActive: true,
    // Location fields
    countryCode: '',
    regionCode: '',
    areaId: '',
    // LINE LIFF
    liffId: '',
  });

  useEffect(() => {
    fetchShops();
    fetchBusinessTypes();
    fetchCountries();
  }, []);

  const fetchShops = async () => {
    try {
      const q = query(collection(db, 'shops'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => {
        const shopData = doc.data();
        return {
          id: doc.id,
          ...shopData,
          createdAt: shopData.createdAt?.toDate ? shopData.createdAt.toDate() : new Date(),
          updatedAt: shopData.updatedAt?.toDate ? shopData.updatedAt.toDate() : new Date(),
        };
      }) as Shop[];
      setShops(data);
    } catch (error) {
      console.error('店舗一覧の取得エラー:', error);
      setError('店舗一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessTypes = async () => {
    try {
      const q = query(collection(db, 'businessTypes'), orderBy('sortOrder', 'asc'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as BusinessType[];
      setBusinessTypes(data.filter(bt => bt.isActive));
    } catch (error) {
      console.error('業種マスターの取得エラー:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      // Firestoreから直接取得（country_mastersコレクション）
      const q = query(
        collection(db, 'country_masters'),
        where('isActive', '==', true),
        orderBy('sortOrder', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as CountryMaster[];
      setCountries(data);
    } catch (error) {
      console.error('国マスターの取得エラー:', error);
    }
  };

  const fetchAreas = async (countryCode: string) => {
    try {
      // Firestoreから直接取得（area_mastersコレクション）
      // 国コードで直接フィルタリング（地域をスキップ）
      const q = query(
        collection(db, 'area_masters'),
        where('countryCode', '==', countryCode),
        where('isActive', '==', true),
        orderBy('sortOrder', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as AreaMaster[];
      setAreas(data);
    } catch (error) {
      console.error('エリアマスターの取得エラー:', error);
    }
  };

  // 店舗コードのバリデーション
  const validateShopCode = (code: string): string | null => {
    if (!code) return '店舗コードは必須です';
    if (code.length < 3 || code.length > 20) return '店舗コードは3〜20文字で入力してください';
    if (!/^[a-zA-Z0-9-_]+$/.test(code)) return '店舗コードは英数字、ハイフン、アンダースコアのみ使用できます';
    return null;
  };

  // 店舗コードの重複チェック
  const checkShopCodeDuplicate = async (code: string, excludeShopId?: string): Promise<boolean> => {
    try {
      const q = query(collection(db, 'shops'), where('shopCode', '==', code));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) return false;

      // 編集中の店舗自身は除外
      if (excludeShopId) {
        return querySnapshot.docs.some(doc => doc.id !== excludeShopId);
      }

      return true;
    } catch (error) {
      console.error('店舗コード重複チェックエラー:', error);
      return false;
    }
  };

  // 店舗コード変更時のハンドラー
  const handleShopCodeChange = async (newCode: string) => {
    setFormData({ ...formData, shopCode: newCode });
    setShopCodeError(null);

    // バリデーション
    const validationError = validateShopCode(newCode);
    if (validationError) {
      setShopCodeError(validationError);
      return;
    }

    // 重複チェック
    const isDuplicate = await checkShopCodeDuplicate(newCode, editingShop?.id);
    if (isDuplicate) {
      setShopCodeError('この店舗コードは既に使用されています');
    }
  };

  const handleToggleStatus = async (shop: Shop) => {
    try {
      const shopRef = doc(db, 'shops', shop.id);
      await updateDoc(shopRef, {
        isActive: !shop.isActive,
        updatedAt: new Date(),
      });
      setSuccess(`店舗を${shop.isActive ? '停止' : '有効化'}しました`);
      fetchShops();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('店舗ステータスの更新エラー:', error);
      setError('店舗ステータスの更新に失敗しました');
    }
  };

  const handleViewDetails = (shop: Shop) => {
    // フォーカスを解除してaria-hidden警告を防ぐ
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setSelectedShop(shop);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedShop(null);
  };

  const handleOpenEditDialog = async (shop?: Shop) => {
    // フォーカスを解除してaria-hidden警告を防ぐ
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (shop) {
      setEditingShop(shop);
      const countryCode = shop.location?.countryCode || '';
      const regionId = shop.location?.regionId || '';
      const areaId = shop.location?.areaId || '';

      setFormData({
        shopCode: shop.shopCode || '',
        shopName: shop.shopName || '',
        name: shop.name || '',
        businessTypeCode: shop.businessTypeCode || '',
        ownerName: shop.ownerName || '',
        ownerEmail: shop.ownerEmail || '',
        ownerPhone: shop.ownerPhone || '',
        ownerPassword: '',
        ownerPasswordConfirm: '',
        postalCode: shop.postalCode || '',
        prefecture: shop.prefecture || '',
        city: shop.city || '',
        address: shop.address || '',
        building: shop.building || '',
        phoneNumber: shop.phoneNumber || '',
        email: shop.email || '',
        websiteUrl: shop.websiteUrl || '',
        description: shop.description || '',
        planId: shop.planId || 'free',
        language: shop.language || 'ja',
        timezone: shop.timezone || 'Asia/Tokyo',
        currency: shop.currency || 'JPY',
        mobileOrderEnabled: shop.mobileOrderEnabled || false,
        isActive: shop.isActive !== false,
        countryCode,
        regionCode: regionId, // formDataのフィールド名はregionCodeだが、実際にはregionIdを保存
        areaId,
        liffId: (shop as any).liffId || '',
      });

      // 国が設定されていればエリアをフェッチ（地域をスキップ）
      if (countryCode) {
        await fetchAreas(countryCode);
      }
    } else {
      setEditingShop(null);
      setAreas([]);
      setFormData({
        shopCode: '',
        shopName: '',
        name: '',
        businessTypeCode: businessTypes[0]?.code || '',
        ownerName: '',
        ownerEmail: '',
        ownerPhone: '',
        ownerPassword: '',
        ownerPasswordConfirm: '',
        postalCode: '',
        prefecture: '',
        city: '',
        address: '',
        building: '',
        phoneNumber: '',
        email: '',
        websiteUrl: '',
        description: '',
        planId: 'free',
        language: 'ja',
        timezone: 'Asia/Tokyo',
        currency: 'JPY',
        mobileOrderEnabled: false,
        isActive: true,
        countryCode: '',
        regionCode: '',
        areaId: '',
        liffId: '',
      });
    }
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingShop(null);
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setShopCodeError(null);

      if (!formData.shopCode || !formData.shopName || !formData.ownerEmail) {
        setError('店舗コード、店舗名、オーナーメールは必須です');
        return;
      }

      // 新規作成時のパスワードチェック
      if (!editingShop) {
        if (!formData.ownerPassword) {
          setError('パスワードは必須です');
          return;
        }
        if (formData.ownerPassword.length < 6) {
          setError('パスワードは6文字以上で入力してください');
          return;
        }
        if (formData.ownerPassword !== formData.ownerPasswordConfirm) {
          setError('パスワードが一致しません');
          return;
        }
      }

      // 店舗コードのバリデーション
      const validationError = validateShopCode(formData.shopCode);
      if (validationError) {
        setShopCodeError(validationError);
        return;
      }

      // 重複チェック
      const isDuplicate = await checkShopCodeDuplicate(formData.shopCode, editingShop?.id);
      if (isDuplicate) {
        setShopCodeError('この店舗コードは既に使用されています');
        return;
      }

      if (editingShop) {
        // メールアドレスが変更されていて、ownerIdがある場合のみCloud Functionを使用
        if (formData.ownerEmail !== editingShop.ownerEmail && editingShop.ownerId) {
          try {
            const updateShop = httpsCallable(functions, 'updateShop');
            const result = await updateShop({
              shopId: editingShop.id,
              shopName: formData.shopName,
              ownerName: formData.ownerName,
              ownerEmail: formData.ownerEmail,
              ownerPhone: formData.ownerPhone,
              postalCode: formData.postalCode,
              prefecture: formData.prefecture,
              city: formData.city,
              address: formData.address,
              building: formData.building || '',
              phoneNumber: formData.phoneNumber,
              email: formData.email,
              websiteUrl: formData.websiteUrl || '',
              description: formData.description || '',
            });
            console.log('updateShop result:', result);
            setSuccess('店舗を更新しました（メールアドレスも同期）');
          } catch (updateError: any) {
            console.error('updateShop error:', updateError);
            throw new Error(`メールアドレスの更新に失敗しました: ${updateError.message || updateError.code || '不明なエラー'}`);
          }
        } else if (formData.ownerEmail !== editingShop.ownerEmail && !editingShop.ownerId) {
          // ownerIdがない場合は警告を出すが続行
          console.warn('店舗にownerIdが設定されていないため、Firebase Authのメールアドレスは更新されません');
        }

        // Firestoreを更新（メールアドレス変更がない場合、または追加フィールドの更新）
        // location情報を構築
        const locationData = formData.countryCode ? {
          countryCode: formData.countryCode,
          regionId: formData.regionCode || null, // formData.regionCodeにはregionIdが入っている
          areaId: formData.areaId || null,
        } : null;

        const updateData: Record<string, any> = {
          shopCode: formData.shopCode,
          shopName: formData.shopName,
          name: formData.shopName,
          businessTypeCode: formData.businessTypeCode,
          ownerName: formData.ownerName,
          ownerEmail: formData.ownerEmail,
          ownerPhone: formData.ownerPhone,
          postalCode: formData.postalCode,
          prefecture: formData.prefecture,
          city: formData.city,
          address: formData.address,
          building: formData.building || '',
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          websiteUrl: formData.websiteUrl || '',
          description: formData.description || '',
          planId: formData.planId,
          plan: formData.planId,
          'subscription.planId': formData.planId,
          'subscription.status': 'active',
          subscriptionStatus: 'active',
          language: formData.language,
          timezone: formData.timezone,
          currency: formData.currency,
          mobileOrderEnabled: formData.mobileOrderEnabled,
          isActive: formData.isActive,
          updatedAt: Timestamp.now(),
        };

        // location情報がある場合のみ更新
        if (locationData) {
          updateData.location = locationData;
        }

        // LIFF IDがある場合のみ更新
        if (formData.liffId) {
          updateData.liffId = formData.liffId;
        }

        await updateDoc(doc(db, 'shops', editingShop.id), updateData);

        if (formData.ownerEmail === editingShop.ownerEmail) {
          setSuccess('店舗を更新しました');
        }
      } else {
        // 新規作成 - Cloud Functionを使用してFirebase Authユーザーも作成
        const now = Timestamp.now();

        // location情報を構築（新規作成時）
        const newLocationData = formData.countryCode ? {
          countryCode: formData.countryCode,
          regionId: formData.regionCode || null, // formData.regionCodeにはregionIdが入っている
          areaId: formData.areaId || null,
        } : null;

        const shopData: Record<string, any> = {
          shopCode: formData.shopCode,
          shopName: formData.shopName,
          name: formData.shopName,
          businessTypeCode: formData.businessTypeCode,
          ownerName: formData.ownerName,
          ownerEmail: formData.ownerEmail,
          ownerPhone: formData.ownerPhone,
          postalCode: formData.postalCode,
          prefecture: formData.prefecture,
          city: formData.city,
          address: formData.address,
          building: formData.building,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          websiteUrl: formData.websiteUrl,
          description: formData.description,
          planId: formData.planId,
          language: formData.language,
          timezone: formData.timezone,
          currency: formData.currency,
          mobileOrderEnabled: formData.mobileOrderEnabled,
          isActive: formData.isActive,
          subscription: {
            planId: formData.planId,
            status: 'active' as const,
            startedAt: now,
            expiresAt: null,
          },
          subscriptionStatus: 'active' as const,
          stats: {
            totalReservations: 0,
            monthlyReservationCount: 0,
          },
          monthlyReservationCount: 0,
          createdAt: now,
          updatedAt: now,
        };

        // location情報がある場合のみ追加
        if (newLocationData) {
          shopData.location = newLocationData;
        }

        // LIFF IDがある場合のみ追加
        if (formData.liffId) {
          shopData.liffId = formData.liffId;
        }

        const createShop = httpsCallable(functions, 'createShopWithOwner');
        await createShop({
          ownerEmail: formData.ownerEmail,
          ownerPassword: formData.ownerPassword,
          shopData,
        });

        setSuccess('店舗とオーナーユーザーを作成しました');
      }

      handleCloseEditDialog();
      fetchShops();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('店舗の保存エラー:', error);
      setError(error.message || '店舗の保存に失敗しました');
    }
  };

  const handleOpenDeleteDialog = (shop: Shop) => {
    // フォーカスを解除してaria-hidden警告を防ぐ
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setDeleteTarget(shop);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDoc(doc(db, 'shops', deleteTarget.id));
      setSuccess('店舗を削除しました');
      handleCloseDeleteDialog();
      fetchShops();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('店舗の削除エラー:', error);
      setError('店舗の削除に失敗しました');
    }
  };

  const handleOpenPasswordDialog = (shop: Shop) => {
    // フォーカスを解除してaria-hidden警告を防ぐ
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setPasswordChangeTarget(shop);
    setPasswordForm({
      newPassword: '',
      confirmPassword: '',
    });
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setPasswordChangeTarget(null);
    setPasswordForm({
      newPassword: '',
      confirmPassword: '',
    });
    setError(null);
  };

  const handlePasswordChange = async () => {
    if (!passwordChangeTarget) return;

    // バリデーション
    if (!passwordForm.newPassword) {
      setError('新しいパスワードを入力してください');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    try {
      // Cloud Functionを呼び出してFirebase Authenticationのパスワードを変更
      const changePassword = httpsCallable(functions, 'changeShopPassword');
      await changePassword({
        shopId: passwordChangeTarget.id,
        newPassword: passwordForm.newPassword,
      });

      setSuccess('パスワードを変更しました');
      handleClosePasswordDialog();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('パスワード変更エラー:', error);
      setError(error.message || 'パスワードの変更に失敗しました');
    }
  };

  const handleOpenExtendPlanDialog = (shop: Shop) => {
    // フォーカスを解除してaria-hidden警告を防ぐ
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setExtendPlanTarget(shop);
    setExtendPlanForm({
      extensionMonths: 1,
      newPlanId: shop.planId,
    });
    setOpenExtendPlanDialog(true);
  };

  const handleCloseExtendPlanDialog = () => {
    setOpenExtendPlanDialog(false);
    setExtendPlanTarget(null);
    setExtendPlanForm({
      extensionMonths: 1,
      newPlanId: '',
    });
    setError(null);
  };

  const handleExtendPlan = async () => {
    if (!extendPlanTarget) return;

    try {
      const shopRef = doc(db, 'shops', extendPlanTarget.id);

      // 現在の有効期限を取得（なければ今日から）
      const currentExpiresAt = extendPlanTarget.subscription?.expiresAt
        ? extendPlanTarget.subscription.expiresAt.toDate()
        : new Date();

      // 有効期限が過去の場合は今日から延長
      const baseDate = currentExpiresAt < new Date() ? new Date() : currentExpiresAt;

      // 延長後の有効期限を計算
      const newExpiresAt = new Date(baseDate);
      newExpiresAt.setMonth(newExpiresAt.getMonth() + extendPlanForm.extensionMonths);

      // Firestoreを更新
      await updateDoc(shopRef, {
        plan: extendPlanForm.newPlanId,  // ポータルサイト用のプラン区分
        planId: extendPlanForm.newPlanId,
        'subscription.planId': extendPlanForm.newPlanId,
        'subscription.status': 'active',
        'subscription.expiresAt': Timestamp.fromDate(newExpiresAt),
        subscriptionStatus: 'active',
        updatedAt: Timestamp.now(),
      });

      setSuccess(`プランを${extendPlanForm.extensionMonths}ヶ月延長しました（新しい有効期限: ${newExpiresAt.toLocaleDateString('ja-JP')}）`);
      handleCloseExtendPlanDialog();
      fetchShops();
      setTimeout(() => setSuccess(null), 5000);
    } catch (error: any) {
      console.error('プラン延長エラー:', error);
      setError(error.message || 'プランの延長に失敗しました');
    }
  };

  const handleSyncOwners = async () => {
    setSyncing(true);
    setError(null);

    try {
      const syncOwners = httpsCallable(functions, 'syncShopOwners');
      const result = await syncOwners() as any;

      console.log('Sync result:', result.data);

      const results = result.data.results || [];
      setSyncResults(results);

      const successCount = results.filter((r: any) => r.status.startsWith('✅')).length;
      const errorCount = results.filter((r: any) => r.status.startsWith('❌')).length;

      setSuccess(`店舗オーナーIDの同期が完了しました（成功: ${successCount}, エラー: ${errorCount}）`);
      setOpenSyncResultDialog(true);
      fetchShops();
    } catch (error: any) {
      console.error('Sync error:', error);
      setError(error.message || '同期に失敗しました');
    } finally {
      setSyncing(false);
    }
  };

  const getBusinessTypeName = (code: string): string => {
    const bt = businessTypes.find(b => b.code === code);
    return bt ? `${bt.icon} ${bt.name}` : code;
  };

  const getSubscriptionStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'active': return 'success';
      case 'trial': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getSubscriptionStatusLabel = (status: string): string => {
    switch (status) {
      case 'active': return '稼働中';
      case 'trial': return 'トライアル';
      case 'suspended': return '停止';
      case 'inactive': return '未契約';
      default: return status;
    }
  };

  // Filter and search shops
  const filteredShops = React.useMemo(() => {
    return shops.filter(shop => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          shop.name.toLowerCase().includes(searchLower) ||
          shop.shopCode.toLowerCase().includes(searchLower) ||
          shop.ownerEmail.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Business type filter
      if (filterBusinessType !== 'all' && shop.businessTypeCode !== filterBusinessType) {
        return false;
      }

      // Status filter
      if (filterStatus !== 'all') {
        if (filterStatus === 'active' && !shop.isActive) return false;
        if (filterStatus === 'inactive' && shop.isActive) return false;
      }

      // Subscription filter
      if (filterSubscription !== 'all' && shop.subscription?.status !== filterSubscription) {
        return false;
      }

      return true;
    });
  }, [shops, searchTerm, filterBusinessType, filterStatus, filterSubscription]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          店舗管理
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" color="text.secondary">
            全{shops.length}店舗 / 表示{filteredShops.length}店舗
          </Typography>
          <Button
            variant="outlined"
            onClick={handleSyncOwners}
            disabled={syncing}
          >
            {syncing ? '同期中...' : 'オーナーID同期'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenEditDialog()}
          >
            店舗を追加
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="店舗名、店舗コード、メールアドレスで検索"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>業種</InputLabel>
            <Select
              value={filterBusinessType}
              label="業種"
              onChange={(e) => setFilterBusinessType(e.target.value)}
            >
              <MenuItem value="all">すべて</MenuItem>
              {businessTypes.map(bt => (
                <MenuItem key={bt.code} value={bt.code}>
                  {bt.icon} {bt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>ステータス</InputLabel>
            <Select
              value={filterStatus}
              label="ステータス"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">すべて</MenuItem>
              <MenuItem value="active">有効</MenuItem>
              <MenuItem value="inactive">停止</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>契約状態</InputLabel>
            <Select
              value={filterSubscription}
              label="契約状態"
              onChange={(e) => setFilterSubscription(e.target.value)}
            >
              <MenuItem value="all">すべて</MenuItem>
              <MenuItem value="active">稼働中</MenuItem>
              <MenuItem value="trial">トライアル</MenuItem>
              <MenuItem value="suspended">停止</MenuItem>
              <MenuItem value="inactive">未契約</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>店舗コード</TableCell>
              <TableCell>店舗名</TableCell>
              <TableCell>業種</TableCell>
              <TableCell>オーナーメール</TableCell>
              <TableCell>契約状態</TableCell>
              <TableCell>月間予約数</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  読み込み中...
                </TableCell>
              </TableRow>
            ) : filteredShops.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {shops.length === 0 ? '店舗がありません' : '条件に一致する店舗がありません'}
                </TableCell>
              </TableRow>
            ) : (
              filteredShops.map((shop) => (
                <TableRow key={shop.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {shop.shopCode}
                    </Typography>
                  </TableCell>
                  <TableCell>{shop.name}</TableCell>
                  <TableCell>{getBusinessTypeName(shop.businessTypeCode)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{shop.ownerEmail}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getSubscriptionStatusLabel(shop.subscription?.status || 'inactive')}
                      color={getSubscriptionStatusColor(shop.subscription?.status || 'inactive')}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{shop.stats?.monthlyReservationCount || 0}</TableCell>
                  <TableCell>
                    <Chip
                      label={shop.isActive ? '有効' : '停止'}
                      color={shop.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetails(shop)}
                      color="primary"
                      title="詳細表示"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(shop)}
                      color="primary"
                      title="編集"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenExtendPlanDialog(shop)}
                      color="info"
                      title="プラン延長"
                    >
                      <CalendarMonthIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleStatus(shop)}
                      color={shop.isActive ? 'error' : 'success'}
                      title={shop.isActive ? '停止' : '有効化'}
                    >
                      {shop.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPasswordDialog(shop)}
                      color="warning"
                      title="パスワード変更"
                    >
                      <KeyIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(shop)}
                      color="error"
                      title="削除"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Add Shop Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingShop ? '店舗を編集' : '店舗を追加'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="店舗コード"
              value={formData.shopCode}
              onChange={(e) => handleShopCodeChange(e.target.value)}
              required
              fullWidth
              error={!!shopCodeError}
              helperText={shopCodeError || '英数字、ハイフン、アンダースコアのみ使用可（3〜20文字）'}
              placeholder="例: my-cafe, ramen-shop123"
            />

            <TextField
              label="店舗名"
              value={formData.shopName}
              onChange={(e) => setFormData({ ...formData, shopName: e.target.value })}
              required
              fullWidth
            />

            <FormControl fullWidth required>
              <InputLabel>業種</InputLabel>
              <Select
                value={formData.businessTypeCode}
                label="業種"
                onChange={(e) => setFormData({ ...formData, businessTypeCode: e.target.value })}
              >
                {businessTypes.map(bt => (
                  <MenuItem key={bt.code} value={bt.code}>
                    {bt.icon} {bt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 国・都市の選択（2階層） */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              ポータルサイト掲載エリア（任意）
            </Typography>

            <FormControl fullWidth>
              <InputLabel>国</InputLabel>
              <Select
                value={formData.countryCode}
                label="国"
                onChange={async (e) => {
                  const countryCode = e.target.value;
                  setFormData({ ...formData, countryCode, regionCode: '', areaId: '' });
                  if (countryCode) {
                    await fetchAreas(countryCode);
                  } else {
                    setAreas([]);
                  }
                }}
              >
                <MenuItem value="">選択してください</MenuItem>
                {countries.map(country => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name.ja}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!formData.countryCode}>
              <InputLabel>都市</InputLabel>
              <Select
                value={formData.areaId}
                label="都市"
                onChange={(e) => setFormData({ ...formData, areaId: e.target.value })}
              >
                <MenuItem value="">選択してください</MenuItem>
                {areas.map(area => (
                  <MenuItem key={area.id} value={area.id}>
                    {area.name.ja}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="オーナー名"
              value={formData.ownerName}
              onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
              fullWidth
            />

            <TextField
              label="オーナーメールアドレス"
              type="email"
              value={formData.ownerEmail}
              onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="オーナー電話番号"
              value={formData.ownerPhone}
              onChange={(e) => setFormData({ ...formData, ownerPhone: e.target.value })}
              fullWidth
            />

            {!editingShop && (
              <>
                <TextField
                  label="初期パスワード"
                  type="password"
                  value={formData.ownerPassword}
                  onChange={(e) => setFormData({ ...formData, ownerPassword: e.target.value })}
                  required
                  fullWidth
                  helperText="6文字以上で入力してください（新規作成時のみ）"
                />

                <TextField
                  label="パスワード確認"
                  type="password"
                  value={formData.ownerPasswordConfirm}
                  onChange={(e) => setFormData({ ...formData, ownerPasswordConfirm: e.target.value })}
                  required
                  fullWidth
                />
              </>
            )}

            <TextField
              label="郵便番号"
              value={formData.postalCode}
              onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
              fullWidth
            />

            <TextField
              label="都道府県"
              value={formData.prefecture}
              onChange={(e) => setFormData({ ...formData, prefecture: e.target.value })}
              fullWidth
            />

            <TextField
              label="市区町村"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              fullWidth
            />

            <TextField
              label="住所"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              fullWidth
            />

            <TextField
              label="建物名・階数"
              value={formData.building}
              onChange={(e) => setFormData({ ...formData, building: e.target.value })}
              fullWidth
            />

            <TextField
              label="店舗電話番号"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              fullWidth
            />

            <TextField
              label="店舗メールアドレス"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />

            <TextField
              label="ウェブサイトURL"
              value={formData.websiteUrl}
              onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
              fullWidth
            />

            <TextField
              label="説明"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>プラン</InputLabel>
              <Select
                value={formData.planId}
                label="プラン"
                onChange={(e) => setFormData({ ...formData, planId: e.target.value })}
              >
                <MenuItem value="free">無料プラン（ポータル掲載のみ）</MenuItem>
                <MenuItem value="starter">スタータープラン（月間予約制限あり）</MenuItem>
                <MenuItem value="standard">スタンダードプラン（予約無制限）</MenuItem>
                <MenuItem value="mobile_order">モバイルオーダーセット</MenuItem>
                <MenuItem value="premium">プレミアムプラン（全機能）</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.mobileOrderEnabled}
                  onChange={(e) => setFormData({ ...formData, mobileOrderEnabled: e.target.checked })}
                  color="primary"
                />
              }
              label="モバイルオーダー機能を有効化（飲食店向け）"
            />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              LINE連携設定（任意）
            </Typography>

            <TextField
              label="LIFF ID"
              value={formData.liffId}
              onChange={(e) => setFormData({ ...formData, liffId: e.target.value })}
              fullWidth
              placeholder="例: 1234567890-abcdefgh"
              helperText="LINE Developers ConsoleでLIFFアプリを作成し、LIFF IDを入力してください"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingShop ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>店舗を削除</DialogTitle>
        <DialogContent>
          <Typography>
            店舗「{deleteTarget?.shopName || deleteTarget?.name}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            ※ この操作は取り消せません。関連する予約データも削除されます。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>パスワード変更</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              店舗: {passwordChangeTarget?.shopName}（{passwordChangeTarget?.shopCode}）
            </Typography>

            <TextField
              label="新しいパスワード"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              required
              fullWidth
              helperText="6文字以上で入力してください"
            />

            <TextField
              label="パスワード確認"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              required
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>キャンセル</Button>
          <Button onClick={handlePasswordChange} variant="contained" color="primary">
            変更
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend Plan Dialog */}
      <Dialog open={openExtendPlanDialog} onClose={handleCloseExtendPlanDialog} maxWidth="sm" fullWidth>
        <DialogTitle>プラン延長</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              店舗: {extendPlanTarget?.shopName}（{extendPlanTarget?.shopCode}）
            </Typography>

            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">現在のプラン</Typography>
              <Typography variant="body1" fontWeight="bold" gutterBottom>
                {extendPlanTarget?.planId}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>現在の有効期限</Typography>
              <Typography variant="body1" gutterBottom>
                {extendPlanTarget?.subscription?.expiresAt
                  ? extendPlanTarget.subscription.expiresAt.toDate().toLocaleDateString('ja-JP')
                  : '期限なし（無制限）'
                }
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>契約状態</Typography>
              <Chip
                label={getSubscriptionStatusLabel(extendPlanTarget?.subscription?.status || 'inactive')}
                color={getSubscriptionStatusColor(extendPlanTarget?.subscription?.status || 'inactive')}
                size="small"
              />
            </Box>

            <FormControl fullWidth required>
              <InputLabel>新しいプラン</InputLabel>
              <Select
                value={extendPlanForm.newPlanId}
                label="新しいプラン"
                onChange={(e) => setExtendPlanForm({ ...extendPlanForm, newPlanId: e.target.value })}
              >
                <MenuItem value="free">無料プラン（ポータル掲載のみ）</MenuItem>
                <MenuItem value="starter">スタータープラン（月間予約制限あり）</MenuItem>
                <MenuItem value="standard">スタンダードプラン（予約無制限）</MenuItem>
                <MenuItem value="mobile_order">モバイルオーダーセット</MenuItem>
                <MenuItem value="premium">プレミアムプラン（全機能）</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>延長期間（月）</InputLabel>
              <Select
                value={extendPlanForm.extensionMonths}
                label="延長期間（月）"
                onChange={(e) => setExtendPlanForm({ ...extendPlanForm, extensionMonths: Number(e.target.value) })}
              >
                <MenuItem value={1}>1ヶ月</MenuItem>
                <MenuItem value={3}>3ヶ月</MenuItem>
                <MenuItem value={6}>6ヶ月</MenuItem>
                <MenuItem value={12}>12ヶ月（1年）</MenuItem>
                <MenuItem value={24}>24ヶ月（2年）</MenuItem>
                <MenuItem value={36}>36ヶ月（3年）</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 1 }}>
              {(() => {
                if (!extendPlanTarget) return '';
                const currentExpiresAt = extendPlanTarget.subscription?.expiresAt
                  ? extendPlanTarget.subscription.expiresAt.toDate()
                  : new Date();
                const baseDate = currentExpiresAt < new Date() ? new Date() : currentExpiresAt;
                const newExpiresAt = new Date(baseDate);
                newExpiresAt.setMonth(newExpiresAt.getMonth() + extendPlanForm.extensionMonths);
                return `新しい有効期限: ${newExpiresAt.toLocaleDateString('ja-JP')}`;
              })()}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExtendPlanDialog}>キャンセル</Button>
          <Button onClick={handleExtendPlan} variant="contained" color="primary">
            延長
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync Result Dialog */}
      <Dialog open={openSyncResultDialog} onClose={() => setOpenSyncResultDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>オーナーID同期結果</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>店舗コード</TableCell>
                  <TableCell>店舗名</TableCell>
                  <TableCell>メールアドレス</TableCell>
                  <TableCell>オーナーID</TableCell>
                  <TableCell>ステータス</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncResults.map((result, index) => (
                  <TableRow key={index}>
                    <TableCell>{result.shopCode}</TableCell>
                    <TableCell>{result.shopName}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{result.ownerEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">
                        {result.ownerId ? result.ownerId.substring(0, 8) + '...' : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={result.status.startsWith('✅') ? 'success.main' : 'error.main'}
                      >
                        {result.status}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSyncResultDialog(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* Shop Details Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>店舗詳細</DialogTitle>
        <DialogContent>
          {selectedShop && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>基本情報</Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">店舗コード</Typography>
                <Typography variant="body1" fontWeight="bold" gutterBottom>{selectedShop.shopCode}</Typography>

                <Typography variant="body2" color="text.secondary">店舗名</Typography>
                <Typography variant="body1" gutterBottom>{selectedShop.name}</Typography>

                <Typography variant="body2" color="text.secondary">業種</Typography>
                <Typography variant="body1" gutterBottom>{getBusinessTypeName(selectedShop.businessTypeCode)}</Typography>

                <Typography variant="body2" color="text.secondary">ステータス</Typography>
                <Chip
                  label={selectedShop.isActive ? '有効' : '停止'}
                  color={selectedShop.isActive ? 'success' : 'default'}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Box>

              <Typography variant="h6" gutterBottom>オーナー情報</Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">オーナーID</Typography>
                <Typography variant="body1" gutterBottom>{selectedShop.ownerId}</Typography>

                <Typography variant="body2" color="text.secondary">メールアドレス</Typography>
                <Typography variant="body1" gutterBottom>{selectedShop.ownerEmail}</Typography>
              </Box>

              <Typography variant="h6" gutterBottom>契約情報</Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">プランID</Typography>
                <Typography variant="body1" gutterBottom>{selectedShop.subscription?.planId || '-'}</Typography>

                <Typography variant="body2" color="text.secondary">契約状態</Typography>
                <Chip
                  label={getSubscriptionStatusLabel(selectedShop.subscription?.status || 'inactive')}
                  color={getSubscriptionStatusColor(selectedShop.subscription?.status || 'inactive')}
                  size="small"
                  sx={{ mb: 1 }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>契約開始日</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedShop.subscription?.startedAt
                    ? (selectedShop.subscription.startedAt instanceof Date
                        ? selectedShop.subscription.startedAt.toLocaleDateString('ja-JP')
                        : selectedShop.subscription.startedAt.toDate?.()?.toLocaleDateString('ja-JP') || '-')
                    : '-'}
                </Typography>

                {selectedShop.subscription?.expiresAt && (
                  <>
                    <Typography variant="body2" color="text.secondary">契約終了日</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedShop.subscription.expiresAt instanceof Date
                        ? selectedShop.subscription.expiresAt.toLocaleDateString('ja-JP')
                        : selectedShop.subscription.expiresAt.toDate?.()?.toLocaleDateString('ja-JP') || '-'}
                    </Typography>
                  </>
                )}
              </Box>

              <Typography variant="h6" gutterBottom>統計情報</Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">総予約数</Typography>
                <Typography variant="body1" gutterBottom>{selectedShop.stats?.totalReservations || 0}</Typography>

                <Typography variant="body2" color="text.secondary">月間予約数</Typography>
                <Typography variant="body1" gutterBottom>{selectedShop.stats?.monthlyReservationCount || 0}</Typography>

                <Typography variant="body2" color="text.secondary">登録日</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedShop.createdAt instanceof Date
                    ? selectedShop.createdAt.toLocaleDateString('ja-JP')
                    : selectedShop.createdAt?.toDate?.()?.toLocaleDateString('ja-JP') || '-'
                  }
                </Typography>

                {selectedShop.updatedAt && (
                  <>
                    <Typography variant="body2" color="text.secondary">更新日</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedShop.updatedAt instanceof Date
                        ? selectedShop.updatedAt.toLocaleDateString('ja-JP')
                        : selectedShop.updatedAt?.toDate?.()?.toLocaleDateString('ja-JP') || '-'
                      }
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Shops;
